import React from "react";
import { useTranslation } from "react-i18next";

import type { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/types";
import { deleteBorder } from "@dashboard/shared/styles";
import { Box, Typography } from "@mui/material";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { DragHandle } from "@shared/DragHandle";
import { DropIndicator } from "@shared/DropIndicator";
import { ExpandToggleButton } from "@shared/ExpandToggleButton";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";

import { useStyles } from "./PolicyCommandItem.style";

export type PolicyCommandDrag = {
    rowRef: React.RefObject<HTMLTableRowElement | null>;
    handleRef: React.RefObject<HTMLSpanElement | null>;
    isDragging: boolean;
    dropEdge: Edge | null;
};

type Props = {
    index: number;
    commandLabels: { primary: string; secondary?: string | null };
    open: number;
    onToggleOpen: () => void;
    readOnly: boolean;
    loading: boolean;
    onEdit: () => void;
    onDeleteClick: () => void;
    rowRef?: PolicyCommandDrag["rowRef"];
    handleRef?: PolicyCommandDrag["handleRef"];
    isDragging?: boolean;
    dropEdge?: Edge | null;
};

export const PolicyCommandRow = ({
    index,
    commandLabels,
    open,
    onToggleOpen,
    readOnly,
    loading,
    onEdit,
    onDeleteClick,
    rowRef,
    handleRef,
    isDragging = false,
    dropEdge = null,
}: Props) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["sidepanels", "pages"]);

    const cellClassName = rowRef ? classes.dragCell : undefined;
    const dropIndicator = dropEdge ? <DropIndicator edge={dropEdge} /> : null;

    return (
        <HoverRow ref={rowRef} clickable={false} className={cx(isDragging && classes.dragging)}>
            <MinimalTableCell align="left" className={cellClassName} style={deleteBorder(index, open)}>
                {dropIndicator}
                <Box style={{ display: "flex", alignItems: "center" }}>
                    <ExpandToggleButton expanded={open === index} onClick={onToggleOpen} className={classes.collapseBtn} />
                    {!readOnly && handleRef ? (
                        <DragHandle
                            ref={handleRef}
                            className={classes.dragHandle}
                            title={t("pages:policiesPage.tooltip.dragToMove")}
                        />
                    ) : null}
                </Box>
            </MinimalTableCell>
            <MinimalTableCell align="center" width={20} className={cellClassName} style={deleteBorder(index, open)}>
                {dropIndicator}
                <Typography>{index + 1}</Typography>
            </MinimalTableCell>
            <MinimalTableCell
                align="left"
                className={cellClassName}
                style={{ ...deleteBorder(index, open), height: "auto", whiteSpace: "normal" }}
            >
                {dropIndicator}
                <Typography noWrap>{commandLabels.primary}</Typography>
                {commandLabels.secondary ? (
                    <Typography variant="caption" color="textSecondary" display="block" noWrap>
                        {commandLabels.secondary}
                    </Typography>
                ) : null}
            </MinimalTableCell>
            <MinimalTableCell align="right" className={cellClassName} style={deleteBorder(index, open)}>
                {dropIndicator}
                {!readOnly ? (
                    <div className={classes.buttonBox}>
                        <SecondaryButton
                            tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.edit") }}
                            onClick={onEdit}
                            className={loading ? classes.iconDisabled : ""}
                            loading={loading}
                            disabled={loading}
                        >
                            <i className="fa-solid fa-fw fa-pencil" />
                        </SecondaryButton>
                        <DangerOutlinedButton
                            onClick={onDeleteClick}
                            tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.delete") }}
                            loading={loading}
                            className={loading ? classes.iconDisabled : ""}
                            disabled={loading}
                        >
                            <i className="fa-solid fa-fw fa-trash" />
                        </DangerOutlinedButton>
                    </div>
                ) : null}
            </MinimalTableCell>
        </HoverRow>
    );
};
